import { LeftArrowIcon } from '@jouzen/ecom-components/icons/LeftArrowIcon';
import { RightArrowIcon } from '@jouzen/ecom-components/icons/RightArrowIcon';
import type { RadialGradientBGProps } from '@jouzen/ecom-components/v3/GradientBG';
import { ProgressCircle } from '@jouzen/ecom-components/v3/ProgressCircle';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
// eslint-disable-next-line no-restricted-imports -- Need to import here for animation bug fix
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

import ImageWrapper from './ImageWrapper';
import type { LandingPageFeature, LandingPageFeaturesTheme } from './types';

export interface FeaturesCarouselProps {
  readonly activeFeatureIndex: number;
  readonly features: LandingPageFeature[];
  readonly imageGradientBg?: RadialGradientBGProps | undefined;
  readonly carouselCountKey: number;
  readonly onNextClick: () => void;
  readonly onPreviousClick: () => void;
  readonly theme: LandingPageFeaturesTheme;
}

const FeaturesCarousel = ({
  activeFeatureIndex,
  carouselCountKey,
  features,
  imageGradientBg,
  onNextClick,
  onPreviousClick,
  theme,
}: FeaturesCarouselProps) => {
  const t = useTranslations();
  const isFirst = activeFeatureIndex === 0;
  const isLast = activeFeatureIndex === features.length - 1;

  return (
    <div aria-roledescription="carousel">
      <ImageWrapper imageGradientBg={imageGradientBg}>
        <AnimatePresence>
          {features.map(
            (feature, i, arr) =>
              activeFeatureIndex === i && (
                <motion.div
                  className="absolute size-full"
                  key={carouselCountKey}
                >
                  <Motion
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute size-full"
                    data-cy="landing-page-features-carousel-slide"
                    initial={{ opacity: 0, y: 100 }}
                    exit={{
                      opacity: 0,
                      y: -100,
                      transition: { delay: 0, duration: 0.75 },
                    }}
                    key={feature.id}
                    transition={{ delay: 0.25, duration: 0.75 }}
                  >
                    <div
                      aria-hidden={activeFeatureIndex !== i}
                      aria-label={t('slider_item_aria_label', {
                        maxNumber: arr.length,
                        number: i + 1,
                      })}
                      aria-roledescription="slide"
                      className="relative size-full"
                      key={feature.id}
                      role="group"
                    >
                      <Image
                        {...feature.image}
                        fill
                        sizes="(max-width: 1439px) 50vw, 720px"
                      />
                    </div>
                  </Motion>
                </motion.div>
              ),
          )}
        </AnimatePresence>
      </ImageWrapper>
      <Motion
        className="flex w-full justify-end"
        initial={{ opacity: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        viewport={{ once: true }}
        whileInView={{ opacity: 1 }}
      >
        <div
          className={cx('relative z-10 mt-6 flex items-center gap-x-9 pr-8', {
            'text-sandstone-200': theme === 'light',
            'text-sandstone-500': theme === 'dark',
          })}
          data-cy="landing-page-features-carousel-nav"
        >
          <div className="flex gap-x-4">
            <ProgressCircle
              current={activeFeatureIndex + 1}
              total={features.length}
              variant={theme}
            />
            <div aria-hidden="true" className="w-6 whitespace-nowrap">
              <Typography Element="span" color="inherit">
                {`${activeFeatureIndex + 1}/${features.length}`}
              </Typography>
            </div>
          </div>
          <div className="flex gap-x-4">
            <button
              aria-label={t('previous_slide_aria_label')}
              className={cx('flex size-10 items-center justify-center p-0', {
                'cursor-not-allowed': isFirst,
                'opacity-30': isFirst,
              })}
              data-cy="landing-page-features-carousel-prev"
              disabled={isFirst}
              onClick={onPreviousClick}
              type="button"
            >
              <LeftArrowIcon />
            </button>
            <button
              aria-label={t('next_slide_aria_label')}
              className={cx('flex size-10 items-center justify-center p-0', {
                'cursor-not-allowed': isLast,
                'opacity-30': isLast,
              })}
              data-cy="landing-page-features-carousel-next"
              disabled={isLast}
              onClick={onNextClick}
              type="button"
            >
              <RightArrowIcon />
            </button>
          </div>
        </div>
      </Motion>
    </div>
  );
};

export default FeaturesCarousel;
